import React, { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Pencil } from "lucide-react";
import QuestionInput, { type Question } from "./QuestionInput";
import Button from "../ui/Button";
import InputField from "../ui/InputField";

type AnswersState = Record<string, string | string[]>;

interface ClientInfo {
  respondentName: string;
  respondentEmail: string;
  respondentPhone: string;
}

interface CardLayoutProps {
  name: string;
  description?: string;
  questions: Question[];
  answers: AnswersState;
  onChange: (id: string, val: string | string[]) => void;
  // Interactive (client) mode passes these; preview mode omits them.
  clientInfo?: ClientInfo;
  setClientInfo?: React.Dispatch<React.SetStateAction<ClientInfo>>;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  preview?: boolean;
}

const isAnswered = (val: string | string[] | undefined) =>
  Array.isArray(val) ? val.length > 0 : val !== "" && val !== null && val !== undefined;

const emailOk = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// A single step is either the contact card, one question, or the final review.
type Step =
  | { type: "contact" }
  | { type: "question"; q: Question; qIndex: number }
  | { type: "review" };

const CardLayout: React.FC<CardLayoutProps> = ({
  name,
  description,
  questions,
  answers,
  onChange,
  clientInfo,
  setClientInfo,
  onSubmit,
  isSubmitting,
  preview = false,
}) => {
  const collectContact = !preview && !!clientInfo && !!setClientInfo;

  const steps = useMemo<Step[]>(() => {
    const s: Step[] = [];
    if (collectContact) s.push({ type: "contact" });
    questions.forEach((q, i) => s.push({ type: "question", q, qIndex: i }));
    s.push({ type: "review" });
    return s;
  }, [collectContact, questions]);

  const [current, setCurrent] = useState(0);
  const step = steps[Math.min(current, steps.length - 1)];
  const totalQuestions = questions.length;
  const progress = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;

  // Can the user leave the current step? (Required fields must be satisfied.)
  const canAdvance = (): boolean => {
    if (preview) return true;
    if (step.type === "contact" && clientInfo) {
      return (
        clientInfo.respondentName.trim() !== "" &&
        emailOk(clientInfo.respondentEmail.trim()) &&
        clientInfo.respondentPhone.trim() !== ""
      );
    }
    if (step.type === "question" && step.q.required) {
      return isAnswered(answers[step.q._id]);
    }
    return true;
  };

  const goNext = () => setCurrent((c) => Math.min(c + 1, steps.length - 1));
  const goPrev = () => setCurrent((c) => Math.max(c - 1, 0));
  const jumpTo = (i: number) => setCurrent(Math.max(0, Math.min(i, steps.length - 1)));

  // Wrap the change handler so single-choice inputs auto-advance (a leading
  // step-by-step form convention). Only forward, only when the step is valid.
  const handleQuestionChange = (q: Question, val: string | string[]) => {
    onChange(q._id, val);
    const single = q.answerType === "radio" || q.answerType === "select" || q.answerType === "date";
    if (!preview && single && isAnswered(val) && current < steps.length - 1) {
      window.setTimeout(() => setCurrent((c) => (c < steps.length - 1 ? c + 1 : c)), 350);
    }
  };

  const advanceDisabled = !canAdvance();

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header + progress */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{name || "Questionnaire"}</h1>
        {description && <p className="text-gray-600 mt-1 text-sm sm:text-base">{description}</p>}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-1.5">
            <span>
              {step.type === "question"
                ? `Question ${step.qIndex + 1} of ${totalQuestions}`
                : step.type === "contact"
                ? "Your details"
                : "Review"}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#2462b2] to-[#1e55a0] rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* The card */}
      <div className="bg-white border border-gray-200 shadow-xl rounded-2xl p-6 sm:p-8 min-h-[280px] flex flex-col">
        {step.type === "contact" && clientInfo && setClientInfo && (
          <div className="space-y-4 flex-1">
            <h2 className="text-lg font-bold text-gray-800">Tell us who you are</h2>
            <InputField
              label="Full Name"
              placeholder="Enter your full name"
              value={clientInfo.respondentName}
              required
              onChange={(e) => setClientInfo((p) => ({ ...p, respondentName: e.target.value }))}
            />
            <InputField
              label="Email Address"
              type="email"
              placeholder="Enter your email"
              value={clientInfo.respondentEmail}
              required
              onChange={(e) => setClientInfo((p) => ({ ...p, respondentEmail: e.target.value }))}
            />
            <InputField
              label="Phone Number"
              type="tel"
              placeholder="Enter your phone number"
              value={clientInfo.respondentPhone}
              required
              onChange={(e) => setClientInfo((p) => ({ ...p, respondentPhone: e.target.value }))}
            />
          </div>
        )}

        {step.type === "question" && (
          <div className="flex-1 flex flex-col">
            <div className="mb-4">
              <span className="inline-flex items-center justify-center w-9 h-9 bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white rounded-full text-sm font-bold mb-3">
                {step.qIndex + 1}
              </span>
              <label className="block text-lg sm:text-xl font-bold text-gray-900">
                {step.q.question || "Untitled question"}
                {step.q.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            </div>
            <QuestionInput
              question={step.q}
              value={answers[step.q._id] ?? (step.q.answerType === "checkbox" ? [] : "")}
              onChange={(val) => handleQuestionChange(step.q, val)}
              disabled={preview}
              required={step.q.required}
            />
            {step.q.required && (
              <p className="text-[11px] text-gray-400 mt-3">This question is required.</p>
            )}
          </div>
        )}

        {step.type === "review" && (
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="text-[#2462b2]" size={22} />
              <h2 className="text-lg font-bold text-gray-800">Review your answers</h2>
            </div>
            <div className="space-y-3">
              {questions.map((q, i) => {
                const a = answers[q._id];
                const answered = isAnswered(a);
                const display = Array.isArray(a) ? a.join(", ") : (a as string);
                return (
                  <div
                    key={q._id}
                    className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {i + 1}. {q.question}
                        {q.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      <p className={`text-sm mt-0.5 ${answered ? "text-gray-700" : "text-gray-400 italic"}`}>
                        {answered ? display : "No answer provided"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => jumpTo(collectContact ? i + 1 : i)}
                      className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#2462b2] hover:underline"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-3 mt-6">
        <button
          type="button"
          onClick={goPrev}
          disabled={current === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <ArrowLeft size={18} /> Previous
        </button>

        {step.type !== "review" ? (
          <button
            type="button"
            onClick={goNext}
            disabled={advanceDisabled}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#2462b2] text-white font-bold hover:bg-[#1e55a0] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Next <ArrowRight size={18} />
          </button>
        ) : preview ? (
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gray-300 text-gray-500 font-bold cursor-not-allowed"
          >
            Submit (Preview Only)
          </button>
        ) : (
          <Button onClick={onSubmit} isLoading={isSubmitting} className="!py-2.5">
            <span className="inline-flex items-center gap-2">
              <Check size={18} /> {isSubmitting ? "Submitting..." : "Submit"}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default CardLayout;
